import React, { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Loading } from '../../../components/loading';
import { setLoading } from '../../../redux/slices/appInfo';
import { setUserInfo } from '../../../redux/actions';
import { syncKeycloakUser } from '../../../api/setting/users';
import { keycloak, getValidToken } from '../../../auth/keycloak';

const RETURN_TO_KEY = 'autotrack_post_login_route';
const DEFAULT_POST_LOGIN_ROUTE = '/work-order';

/**
 * Post-Keycloak-redirect landing page.
 *
 * Flow:
 *  1. Force a fresh token via `updateToken(-1)` (no min-validity → always refresh).
 *     The access token stays in Keycloak memory only — never written to
 *     localStorage (XSS). API calls read it via `getValidToken` / `keycloak.token`.
 *  2. Project non-secret JWT claims onto the Redux `userInfo` slice
 *     (profile/roles; token is omitted).
 *  3. POST /api/users/sync — BE reads the same claims off the JWT and
 *     upserts the local `User` row by email. Idempotent.
 *  4. Navigate to /work-order.
 *
 * Failure modes:
 *  - Not authenticated → bounce to /login (Keycloak redirect didn't complete).
 *  - /api/users/sync fails (network/5xx) → still navigate to /work-order; the
 *    app degrades gracefully because most pages only need the JWT claims,
 *    not the local user row. The next navigation will retry on /api/users.
 */
export const AuthCallbackPage: React.FC = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    useEffect(() => {
        let cancelled = false;

        const run = async () => {
            dispatch(setLoading(true));
            try {
                // 1. Make sure we have a fresh access token (Keycloak memory only).
                const token = await getValidToken(-1);
                if (!token || !keycloak.authenticated) {
                    throw new Error(
                        'Not authenticated after Keycloak redirect'
                    );
                }

                // 2. Hydrate Redux from JWT claims only — never persist the
                //    access token. tokenParsed is the parsed Keycloak token;
                //    cast to a loose shape because keycloak-js types claims as any.
                const claims = (keycloak.tokenParsed ?? {}) as Record<
                    string,
                    unknown
                >;
                const roles = Array.isArray(
                    (claims.realm_access as { roles?: string[] } | undefined)
                        ?.roles
                )
                    ? (claims.realm_access as { roles: string[] }).roles
                    : null;
                const fullName =
                    typeof claims.name === 'string' ? claims.name : undefined;
                const firstName =
                    typeof claims.given_name === 'string'
                        ? claims.given_name
                        : undefined;
                const lastName =
                    typeof claims.family_name === 'string'
                        ? claims.family_name
                        : undefined;

                if (cancelled) return;

                dispatch(
                    setUserInfo({
                        email:
                            typeof claims.email === 'string'
                                ? claims.email
                                : undefined,
                        username:
                            typeof claims.preferred_username === 'string'
                                ? claims.preferred_username
                                : fullName,
                        FirstName: firstName,
                        LastName: lastName,
                        Id:
                            typeof claims.sub === 'string'
                                ? claims.sub
                                : undefined,
                        UserIdentifier:
                            typeof claims.sub === 'string'
                                ? claims.sub
                                : undefined,
                        roles,
                    })
                );

                // 3. Provision the local user row. If this fails (e.g. 401 from
                //    the API because the bearer token is invalid), bounce back to
                //    /login — otherwise the user lands on /work-order with no
                //    local user row AND no valid token, and the AuthGuard will
                //    re-trigger keycloak.login() on every navigation, causing
                //    the infinite loop you reported.
                try {
                    const syncRes = await syncKeycloakUser();
                    if (syncRes?.status >= 400) {
                        throw new Error(
                            `/api/users/sync returned ${syncRes.status}`
                        );
                    }
                    const syncData = syncRes.data;
                    dispatch(
                        setUserInfo({
                            userId: syncData?.userId,
                            role: syncData?.role,
                            organizationId: syncData?.organizationId,
                            organizationName: syncData?.organizationName,
                        })
                    );
                } catch (syncErr) {
                    // eslint-disable-next-line no-console
                    console.error(
                        '[auth/callback] /api/users/sync failed',
                        syncErr
                    );
                    throw syncErr; // bubble to outer catch -> /login
                }

                if (cancelled) return;
                // Honor the returnTo URL stashed by the guard that
                // triggered login (e.g. /work-order, /settings/cars).
                // Falls back to /work-order when the user logged in
                // via the bare LoginPage button.
                const returnTo =
                    sessionStorage.getItem(RETURN_TO_KEY) ||
                    DEFAULT_POST_LOGIN_ROUTE;
                sessionStorage.removeItem(RETURN_TO_KEY);
                navigate(returnTo, { replace: true });
            } catch (err) {
                // eslint-disable-next-line no-console
                console.error('[auth/callback] failed', err);
                if (cancelled) return;
                navigate('/login', { replace: true });
            } finally {
                if (!cancelled) dispatch(setLoading(false));
            }
        };

        void run();

        return () => {
            cancelled = true;
        };
    }, [dispatch, navigate]);

    // We never render interactive UI on this page — Keycloak's redirect
    // returns here as a normal navigation, and we redirect onward within
    // a few hundred ms. The Loading screen is enough.
    return <Loading />;
};
